import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useGetPlacementCandidate } from "../../../PlacementCandidate/api/useGetPlacementCandidate";
import { isPlacementCandidate } from "../../../PlacementCandidate/types";
import { type PlacementDetail } from "../../api/useGetPlacement";
import { usePayTransparency } from "../PlacementDetailsPage/hooks/usePayTransparency";
import { PlacementApplicationPreferenceFormDialogUI } from "./PlacementApplicationPreferenceFormDialogUI";
import { PlacementDetailsProvider } from "./PlacementDetailsContext";
import {
  type ApplicationPreferencesFormData,
  applicationPreferencesSchema,
} from "./WorkerPreferencesStep";

interface PlacementApplicationPreferenceFormDialogProps {
  modalState: UseModalState;
  placementCandidateId: string;
  applicationQualification: string | undefined;
  placementDetails: PlacementDetail;
  onSavePreferences: (data: ApplicationPreferencesFormData) => void;
}

export function PlacementApplicationPreferenceFormDialog(
  props: PlacementApplicationPreferenceFormDialogProps
) {
  const {
    modalState,
    placementCandidateId,
    applicationQualification,
    placementDetails,
    onSavePreferences,
  } = props;

  const worker = useDefinedWorker();

  const { data: placementCandidate } = useGetPlacementCandidate(
    { workerId: worker.userId },
    { enabled: Boolean(worker.userId) }
  );

  const { isPayTransparencyAvailable, transparencyPayRates } = usePayTransparency(placementDetails);

  type Step = "workerPreferences" | "payDetails";

  const steps: Step[] =
    isPayTransparencyAvailable &&
    isPlacementCandidate(placementCandidate) &&
    transparencyPayRates.length > 0
      ? ["workerPreferences", "payDetails"]
      : ["workerPreferences"];

  const [step, setStep] = useState<Step>(steps[0]);

  const formMethods = useForm<ApplicationPreferencesFormData>({
    resolver: zodResolver(applicationPreferencesSchema),
    mode: "onChange",
    defaultValues: {
      jobType: undefined,
      shiftTime: undefined,
      daysOfWeek: undefined,
      additionalNotes: undefined,
      yearsOfExperience:
        isPlacementCandidate(placementCandidate) &&
        isDefined(applicationQualification) &&
        isDefined(placementCandidate.yearsOfExperienceByQualification) &&
        isDefined(placementCandidate.yearsOfExperienceByQualification[applicationQualification])
          ? {
              [applicationQualification]:
                placementCandidate.yearsOfExperienceByQualification[applicationQualification],
            }
          : undefined,
    },
  });
  return (
    <PlacementDetailsProvider placementDetails={placementDetails}>
      <FormProvider {...formMethods}>
        <PlacementApplicationPreferenceFormDialogUI
          modalState={modalState}
          placementCandidateId={placementCandidateId}
          applicationQualification={applicationQualification}
          steps={steps}
          step={step}
          setStep={setStep}
          placementId={placementDetails.id}
          placementApplicationMethod={placementDetails.applicationMethod}
          workerId={worker.userId}
          workplaceId={placementDetails.workplace.id}
          onSavePreferences={onSavePreferences}
        />
      </FormProvider>
    </PlacementDetailsProvider>
  );
}
